import useNow from '@/hooks/useNow';
import { dayCounter, CARE_PERIOD_DAYS } from '@/logic/carePeriodClock';

// ⚠️ The arc is calendar time, not attainment — it fills whether or not the patient is meeting a
// goal, so the number inside is the DAY and never a percentage. Days left and the two dates ride
// the tooltip and the accessible name, which is why the header needs no line of text beneath it.

const R = 26;
const CIRC = 2 * Math.PI * R;

// Fixed: .pf-identity__pills stacks two badges to exactly this height.
const SIZE = 52;

interface CarePeriodRingProps {
  carePeriod?: { status?: string | null; start_date?: string | null; cms_alignment_date?: string | null } | null;
  now?: number;
}

export default function CarePeriodRing({ carePeriod, now }: CarePeriodRingProps) {
  const at = useNow();
  const c = dayCounter(carePeriod, now ?? at);
  if (c.state === 'unknown') return null;

  const dormant = c.state === 'not_aligned';
  const closed = c.state === 'closed';
  const arc = closed ? 'var(--wi-text-placeholder)' : 'var(--wi-action-primary)';
  const ink = closed || dormant ? 'var(--wi-text-placeholder)' : 'var(--wi-text-primary)';
  const full = `${c.label}${c.sublabel ? ` · ${c.sublabel}` : ''}`;

  return (
    <div
      className="care-ring wi-tip"
      data-tip={full}
    >
      <svg width={SIZE} height={SIZE} viewBox="0 0 64 64" role="img" aria-label={full}>
        <circle
          cx="32"
          cy="32"
          r={R}
          fill="none"
          strokeWidth="5"
          stroke="var(--wi-divider)"
          strokeDasharray={dormant ? '3 5' : undefined}
        />
        {!dormant && (
          <circle
            cx="32"
            cy="32"
            r={R}
            fill="none"
            strokeWidth="5"
            strokeLinecap="round"
            stroke={arc}
            strokeDasharray={CIRC}
            strokeDashoffset={CIRC * (1 - (c.pct ?? 0))}
            transform="rotate(-90 32 32)"
          />
        )}
      </svg>
      <span className="care-ring__inner" style={{ color: ink }}>
        {dormant ? (
          <span className="care-ring__day" aria-hidden="true">&ndash;</span>
        ) : (
          <>
            <span className="care-ring__day" aria-hidden="true">{c.day ?? CARE_PERIOD_DAYS}</span>
            <span className="care-ring__cap" aria-hidden="true">DAY</span>
          </>
        )}
      </span>
    </div>
  );
}
